import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Download, Copy, Key, Settings2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { copyToClipboard } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

interface UserSettings {
  id: string;
  email: string;
  username: string;
  apiToken: string;
  diskQuota: number;
  diskUsed: number;
}

interface UserPreferences {
  defaultUploadPrivacy: boolean;
  emailNotifications: boolean;
  theme: 'light' | 'dark' | 'system';
  uploadsPerPage: number;
}

export function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const fetchSettings = async () => {
    try {
      const data = await api.getCurrentUser();

      // Get full user details including API token
      const userData = await api.getUser(data.id);
      setSettings(userData as UserSettings);

      // Fetch user preferences
      const prefsData = await api.getPreferences();
      setPreferences(prefsData.preferences as UserPreferences);
    } catch (_error) {
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreference = async (updates: Partial<UserPreferences>) => {
    setIsSavingPrefs(true);
    try {
      const data = await api.updatePreferences(updates);
      setPreferences(data.preferences as UserPreferences);
      toast.success('Preferences updated');
    } catch (_error) {
      toast.error('Failed to update preferences');
    } finally {
      setIsSavingPrefs(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleDownloadShareXConfig = async () => {
    try {
      await api.downloadShareXConfig(settings?.username || 'user');
      toast.success('ShareX config downloaded');
    } catch (_error) {
      toast.error('Failed to download ShareX config');
    }
  };

  const handleCopyApiToken = () => {
    if (settings?.apiToken) {
      copyToClipboard(settings.apiToken);
      toast.success('API token copied to clipboard');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsChangingPassword(true);
    try {
      await api.changePassword(user?.id || '', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            <p className="mt-2 text-muted-foreground">
              Manage your account settings and ShareX integration
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* User Preferences */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5" />
                  Preferences
                </CardTitle>
                <CardDescription>Customize your experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Default Upload Privacy */}
                  <div className="flex items-center justify-between space-x-4">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="default-privacy">Default Upload Privacy</Label>
                      <p className="text-sm text-muted-foreground">
                        New uploads are public by default
                      </p>
                    </div>
                    <Switch
                      id="default-privacy"
                      checked={preferences?.defaultUploadPrivacy ?? true}
                      onCheckedChange={(checked) =>
                        updatePreference({ defaultUploadPrivacy: checked })
                      }
                      disabled={isSavingPrefs}
                    />
                  </div>

                  {/* Email Notifications */}
                  <div className="flex items-center justify-between space-x-4">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="email-notifications">Email Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive email updates and alerts
                      </p>
                    </div>
                    <Switch
                      id="email-notifications"
                      checked={preferences?.emailNotifications ?? true}
                      onCheckedChange={(checked) =>
                        updatePreference({ emailNotifications: checked })
                      }
                      disabled={isSavingPrefs}
                    />
                  </div>

                  {/* Theme */}
                  <div className="space-y-2">
                    <Label htmlFor="theme">Theme</Label>
                    <select
                      id="theme"
                      value={preferences?.theme || 'system'}
                      onChange={(e) =>
                        updatePreference({ theme: e.target.value as 'light' | 'dark' | 'system' })
                      }
                      disabled={isSavingPrefs}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="system">System</option>
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>

                  {/* Uploads Per Page */}
                  <div className="space-y-2">
                    <Label htmlFor="uploads-per-page">Uploads Per Page</Label>
                    <Input
                      id="uploads-per-page"
                      type="number"
                      min={10}
                      max={100}
                      value={preferences?.uploadsPerPage || 20}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (value >= 10 && value <= 100) {
                          updatePreference({ uploadsPerPage: value });
                        }
                      }}
                      disabled={isSavingPrefs}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Change Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="current-password">Current Password</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) =>
                          setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                        }
                        required
                        disabled={isChangingPassword}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) =>
                          setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                        }
                        minLength={8}
                        required
                        disabled={isChangingPassword}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) =>
                          setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                        }
                        minLength={8}
                        required
                        disabled={isChangingPassword}
                      />
                    </div>
                  </div>
                  <Button type="submit" disabled={isChangingPassword}>
                    {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* ShareX Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  ShareX Configuration
                </CardTitle>
                <CardDescription>
                  Download your personalized ShareX configuration file
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    This config file contains your API token and upload endpoint. Import it into
                    ShareX to start uploading files directly from your desktop.
                  </p>
                  <ol className="list-inside list-decimal space-y-1 text-sm text-muted-foreground">
                    <li>Download the .sxcu file below</li>
                    <li>Double-click the file to import it into ShareX</li>
                    <li>ShareX will automatically configure the uploader</li>
                    <li>Start uploading screenshots and files!</li>
                  </ol>
                  <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                    <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                      ⚠️ ShareX Limitation
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      ShareX has a 100MB upload limit (Cloudflare restriction). For larger files,
                      use the web uploader or the PowerShell script in{' '}
                      <code className="rounded bg-muted px-1 py-0.5 text-xs">
                        resources/uploaders/
                      </code>
                    </p>
                  </div>
                </div>
                <Button onClick={handleDownloadShareXConfig} className="w-full">
                  <Download className="mr-2 h-4 w-4" />
                  Download ShareX Config
                </Button>
              </CardContent>
            </Card>

            {/* API Token */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  API Token
                </CardTitle>
                <CardDescription>Your personal API token for programmatic uploads</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="apiToken">API Token</Label>
                  <div className="flex gap-2">
                    <Input
                      id="apiToken"
                      type="password"
                      value={settings?.apiToken || ''}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyApiToken}
                      title="Copy API Token"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use this token with the X-API-Token header for API uploads
                  </p>
                </div>

                <div className="border-t pt-4">
                  <h4 className="mb-2 text-sm font-medium">Upload Endpoint</h4>
                  <code className="block break-all rounded bg-muted p-2 text-xs">
                    POST {window.location.origin}/api/upload
                  </code>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Include your API token in the X-API-Token header
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Account Information */}
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={settings?.email || ''} readOnly />
                </div>
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input value={settings?.username || ''} readOnly />
                </div>
              </CardContent>
            </Card>

            {/* Storage Usage */}
            <Card>
              <CardHeader>
                <CardTitle>Storage Usage</CardTitle>
                <CardDescription>Monitor your disk quota</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Used</span>
                    <span className="font-medium">
                      {((settings?.diskUsed || 0) / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Quota</span>
                    <span className="font-medium">
                      {((settings?.diskQuota || 0) / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-primary transition-all"
                      style={{
                        width: `${Math.min(100, ((settings?.diskUsed || 0) / (settings?.diskQuota || 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {(((settings?.diskUsed || 0) / (settings?.diskQuota || 1)) * 100).toFixed(1)}%
                    used
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
